import type { CreateTeamMember, TeamMember, TeamMemberList, UpdateTeamMember } from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest } from './client';
import { mcpConnectionsQueryKey } from './mcp-connections';

/** Chiave della query dell'elenco del team. */
export const teamQueryKey = ['team', 'members'] as const;

/** `GET /team/members`: membri attivi con il carico corrente. */
export function fetchTeamMembers(): Promise<TeamMemberList> {
  return apiRequest<TeamMemberList>('/team/members');
}

/** Elenco dei membri attivi con il carico corrente. */
export function useTeamMembers() {
  return useQuery({ queryKey: teamQueryKey, queryFn: fetchTeamMembers });
}

/** Invalida l'elenco dopo ogni modifica: il carico e l'ordine li decide l'API. */
function useInvalidateTeam() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: teamQueryKey });
}

/** `POST /team/members`: aggiunge una persona al team (409 se l'email Google è già presente). */
export function useAddTeamMember() {
  const invalidate = useInvalidateTeam();
  return useMutation<TeamMember, ApiError, CreateTeamMember>({
    mutationFn: (input) => apiRequest<TeamMember>('/team/members', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

/** `PATCH /team/members/:id`: nome, email di notifica, ruolo o livello. */
export function useUpdateTeamMember() {
  const invalidate = useInvalidateTeam();
  return useMutation<TeamMember, ApiError, { id: string; changes: UpdateTeamMember }>({
    mutationFn: ({ id, changes }) =>
      apiRequest<TeamMember>(`/team/members/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    onSuccess: invalidate,
  });
}

/** Il server ha revocato le connessioni MCP della persona: gli elenchi aperti vanno riletti (#152). */
function useInvalidateMcpConnections() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: mcpConnectionsQueryKey });
}

/** `DELETE /team/members/:id`: rimuove dal team (409 se responsabile di ticket aperti) e ne chiude sessioni e connessioni MCP. */
export function useRemoveTeamMember() {
  const invalidate = useInvalidateTeam();
  const invalidateMcp = useInvalidateMcpConnections();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiRequest<void>(`/team/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => Promise.all([invalidate(), invalidateMcp()]),
  });
}

/** `POST /team/members/:id/sessions/revoke`: chiude sessioni e connessioni MCP della persona, che resta nel team. */
export function useRevokeMemberSessions() {
  const invalidateMcp = useInvalidateMcpConnections();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiRequest<void>(`/team/members/${id}/sessions/revoke`, { method: 'POST' }),
    onSuccess: invalidateMcp,
  });
}
